import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModalDialog from '@atlaskit/modal-dialog';
import ReactDOM from 'react-dom';
import { styles } from './styles';
import MessageInput from './MessageInput';
import MessagesList from './MessagesList';
import { useReportChannel } from './useReportChannel';
import {
  getChatMessages, getIsChatFetching, getTokensInfo, getIsChatWaiting,
} from '../../../redux/chat/selectors';
import SocketErrorNotification from './SocketErrorNotification';
import { chatActions } from '../../../redux/chat/actions';
import { getIsWaiting } from '../../../redux/ws/selectors';
import { DotsAnimation } from './DotsAnimation';
import ChatControls from './ChatControls';

// eslint-disable-next-line react/prop-types
const ChatPage = ({ reportId, query }) => {
  const [value, setValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const refInput = useRef(null);
  const chatRef = useRef(null);
  const dispatch = useDispatch();
  const {
    handleSendMessage, channel, fetchData, reFetchData,
  } = useReportChannel(reportId, query);
  const tokensInfo = useSelector(getTokensInfo);
  const isFetching = useSelector(getIsChatFetching);
  const reportData = channel && channel.data[0];
  const messages = useSelector(getChatMessages);
  const isWaiting = useSelector(getIsWaiting);
  const isChatWaiting = useSelector(getIsChatWaiting);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (isMobile) {
    return (
      <></>
    );
  }

  const scrollToBottom = () => {
    const anchor = document.getElementById('anchor');
    if (anchor) {
      anchor.scrollIntoView();
    }
  };

  const handleData = () => {
    if (!channel.data || !channel.data.length) {
      fetchData();
      return;
    }
    reFetchData();
  };

  const confirmDeleteChannel = () => {
    setIsModalOpen(true);
  };

  const deleteChannel = () => {
    dispatch(chatActions.deleteChannel(channel.id));
    setIsModalOpen(false);
  };

  const renderControlsPortal = () => {
    const container = document.getElementById('chat-controls-portal');
    if (!container || !reportId || !channel) return null;

    return ReactDOM.createPortal(
      <ChatControls
        tokensInfo={tokensInfo}
        reportData={reportData}
        confirmDeleteChannel={confirmDeleteChannel}
        isFetching={isFetching}
        handleData={handleData}
        deleteChannel={deleteChannel}
      />,
      container,
    );
  };


  if (!reportId) {
    return null;
  }

  return (
    <div
      ref={chatRef}
      style={{
        ...styles.root,
        minHeight: messages.length ? '570px' : 'unset',
      }}
    >
      <div style={styles.dataActions}>{renderControlsPortal()}</div>
      <MessagesList
        messages={messages}
        scrollToBottom={scrollToBottom}
        value={value}
        setValue={setValue}
        reportId={reportId}
      />
      {(isWaiting || isChatWaiting) && <DotsAnimation />}
      {reportData && (
        <div ref={refInput} style={{ width: '100%' }}>
          <MessageInput
            value={value}
            setValue={setValue}
            handleSendMessage={handleSendMessage}
            reportId={reportId}
            chatId={channel.id}
          />
        </div>
      )}
      <SocketErrorNotification setValue={setValue} messages={messages} />
      {isModalOpen && (
        <ModalDialog
          heading="Confirm Deletion"
          onClose={() => setIsModalOpen(false)}
          actions={[
            { text: 'Cancel', onClick: () => setIsModalOpen(false) },
            { text: 'Delete', onClick: deleteChannel, appearance: 'danger' },
          ]}
        >
            Are you sure you want to delete this channel?
        </ModalDialog>
      )}
    </div>
  );
};

export default ChatPage;
